import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Row, Col, Form, Select, DatePicker, Button, Alert, Table, Spin, Space, Statistic } from 'antd';
import { FileTextOutlined, SearchOutlined, FilePdfOutlined, FileExcelOutlined, PrinterOutlined, LogoutOutlined, BarChartOutlined, ShoppingCartOutlined, HomeOutlined } from '@ant-design/icons';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './ClubStoreManagerReport.css';
import axios from 'axios';
import { getHeaders } from '../utility/getHeader';
// import fetchData from '../functions/fetchData'; // Old API - Commented out
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

const ClubStoreManagerReport = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [dataLoading, setDataLoading] = useState(false);
  const [filterParams, setFilterParams] = useState(null);

  // Get user information
  const userType = (localStorage.getItem('usertype') || sessionStorage.getItem('usertype') || '').toLowerCase();
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');

  // Check if user is authenticated and is a club store manager
  const isClubStoreManager = userType && (userType === 'cstoremanager' || userType === 'club_store_manager');
  const isAuthenticated = !!token;

  React.useEffect(() => {
    // Check authentication
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    // Check if user is club store manager
    if (!isClubStoreManager) {
      setAccessDenied(true);
      return;
    }
  }, [isAuthenticated, isClubStoreManager, navigate]);

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    toast.success('Logged out successfully!');
    navigate('/login');
  };

  // Export to PDF
  const exportToPDF = () => {
    if (!reportData || reportData.length === 0) {
      toast.error('No data to export');
      return;
    }

    const doc = new jsPDF();
    const now = new Date();
    const generatedDateTime = now.toLocaleString();

    // Add header
    doc.setFontSize(14);
    doc.text('Club Store Manager Report', 14, 15);
    
    doc.setFontSize(10);
    doc.text(`Generated: ${generatedDateTime}`, 14, 25);
    doc.text(`Period: ${filterParams.fromDate} to ${filterParams.toDate}`, 14, 32);
    doc.text(`Type: ${filterParams.type.toUpperCase()} | Zone: ${filterParams.zone.toUpperCase()} | Sale Type: ${filterParams.saleType.toUpperCase()}`, 14, 39);

    // Sort data alphabetically by item_name
    const sortedData = [...reportData].sort((a, b) => a.item_name.localeCompare(b.item_name));

    // Add table
    autoTable(doc, {
      head: [['S.No.', 'Item Name', 'Total Quantity']],
      body: sortedData.map((item, index) => [
        index + 1,
        item.item_name,
        item.total_quantity
      ]),
      startY: 50,
      margin: 14,
      theme: 'grid',
      headerStyles: { fillColor: [41, 128, 185], textColor: 255 }
    });

    doc.save(`Club_Store_Report_${now.getTime()}.pdf`);
    toast.success('PDF exported successfully!');
  };

  // Export to Excel
  const exportToExcel = () => {
    if (!reportData || reportData.length === 0) {
      toast.error('No data to export');
      return;
    }

    // Sort data alphabetically by item_name
    const sortedData = [...reportData].sort((a, b) => a.item_name.localeCompare(b.item_name));

    const ws = XLSX.utils.json_to_sheet(
      sortedData.map((item, index) => ({
        'S.No.': index + 1,
        'Item Name': item.item_name,
        'Total Quantity': item.total_quantity
      }))
    );

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Report');
    
    const now = new Date();
    XLSX.writeFile(wb, `Club_Store_Report_${now.getTime()}.xlsx`);
    toast.success('Excel exported successfully!');
  };

  // Thermal Print
  const thermalPrint = () => {
    if (!reportData || reportData.length === 0) {
      toast.error('No data to print');
      return;
    }

    // Sort data alphabetically by item_name
    const sortedData = [...reportData].sort((a, b) => a.item_name.localeCompare(b.item_name));

    const printWindow = window.open('', '_blank');
    const now = new Date();
    const generatedDateTime = now.toLocaleString();
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const dayName = dayNames[now.getDay()];
    const username = localStorage.getItem('uname') || sessionStorage.getItem('uname') || 'N/A';

    let html = `
      <html>
      <head>
        <title>Club Store Report</title>
        <style>
          body { 
            font-family: 'Arial', sans-serif; 
            font-size: 18px; 
            margin: 15px 0px 15px 15px; 
            padding: 15px 0px 15px 15px; 
            width: 80mm; 
            background: #fff;
          }
          .header { 
            text-align: center; 
            border-bottom: 2px solid #000; 
            border-top: 2px solid #000;
            padding: 15px 10px; 
            margin-bottom: 15px; 
          }
          .header h1 { 
            margin: 0; 
            font-size: 24px; 
            font-weight: bold;
            letter-spacing: 1px;
          }
          .header h2 { 
            margin: 5px 0 0 0; 
            font-size: 22px; 
            font-weight: bold;
          }
          .divider { 
            border-bottom: 1px dashed #000; 
            margin: 12px 0; 
          }
          .info { 
            font-size: 17px; 
            margin: 8px 0;
            padding: 3px 5px;
            line-height: 1.5;
          }
          .info-label {
            font-weight: bold;
            display: inline-block;
            width: 70px;
          }
          table { 
            width: 100%; 
            border-collapse: collapse; 
            margin: 15px 0; 
          }
          th { 
            border-bottom: 1px solid #000; 
            text-align: left; 
            padding: 6px 0px 6px 5px; 
            font-weight: bold;
            font-size: 17px;
          }
          td { 
            border-bottom: 1px dotted #999; 
            padding: 6px 0px 6px 5px;
            font-size: 17px;
          }
          .total { 
            text-align: right; 
            font-weight: bold; 
            border-top: 2px solid #000; 
            margin: 15px 0;
            padding: 12px 5px;
            font-size: 18px;
          }
          @media print { 
            body { margin: 10px 0px 10px 10px; padding: 10px 0px 10px 10px; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>═══════════════════</h1>
          <h2>CLUB STORE REPORT</h2>
          <h1>═══════════════════</h1>
        </div>
        
        <div class="divider"></div>
        
        <div style="display: flex; justify-content: space-between; margin-bottom: 5px;">
          <div style="width: 48%;">
            <div class="info">
              <span class="info-label">User:</span> ${username}
            </div>
            <div class="info">
              <span class="info-label">Day:</span> ${dayName}
            </div>
            <div class="info">
              <span class="info-label">Type:</span> ${filterParams.type.toUpperCase()}
            </div>
          </div>
          <div style="width: 48%;">
            <div class="info">
              <span class="info-label">Zone:</span> ${filterParams.zone.toUpperCase()}
            </div>
            <div class="info">
              <span class="info-label">Sale:</span> ${filterParams.saleType === 'all' ? 'ALL TYPES' : filterParams.saleType.toUpperCase()}
            </div>
          </div>
        </div>
        
        <div class="info">
          <span class="info-label">Generated:</span> ${generatedDateTime}
        </div>
        <div class="info">
          <span class="info-label">Period:</span> ${filterParams.fromDate} to ${filterParams.toDate}
        </div>
        
        <div class="divider"></div>
        
        <table>
          <tr>
            <th style="width: 8%; text-align: center; padding-right: 0;">No.</th>
            <th style="width: 72%; padding-right: 0;">Item Name</th>
            <th style="text-align: right; width: 20%; padding-right: 0;">Qty</th>
          </tr>
    `;

    let totalQty = 0;
    sortedData.forEach((item, index) => {
      html += `
        <tr>
          <td style="text-align: center; width: 8%; padding-right: 0;">${index + 1}</td>
          <td style="width: 82%; padding-right: 0;">${item.item_name}</td>
          <td style="text-align: right; width: 10%; padding-right: 0;">${item.total_quantity}</td>
        </tr>
      `;
      totalQty += item.total_quantity;
    });

    html += `
        </table>
        
        <div class="divider"></div>
        
        <div class="total">
          ✓ Items: ${sortedData.length} | Total Qty: ${totalQty}
        </div>
        
        <div style="text-align: center; margin-top: 8px; font-size: 10px;">
          <p style="margin: 2px 0;">Thank You!</p>
          <p style="margin: 0;">═════════════════════</p>
        </div>
      </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.print();
    
    // Close the window after print dialog is handled
    setTimeout(() => {
      printWindow.close();
    }, 500);
    
    toast.success('Thermal print initiated!');
  };

  const handleSearch = async (values) => {
    try {
      setLoading(true);
      setDataLoading(true);
      
      // Validate form
      if (!values.type || !values.zone || !values.dateRange || !values.saleType) {
        toast.error('Please fill in all fields');
        setLoading(false);
        setDataLoading(false);
        return;
      }

      const [fromDate, toDate] = values.dateRange;
      const fromDateStr = fromDate.format('YYYY-MM-DD');
      const toDateStr = toDate.format('YYYY-MM-DD');

      // Use new API endpoint for item sales summary
      const response = await axios.get('/stock/item-sales-summary', {
        params: {
          start_date: fromDateStr,
          end_date: toDateStr
        },
        ...getHeaders()
      });

      if (response.data.success && response.data.data && response.data.data.length > 0) {
        // Transform API data to match component format (already grouped by backend)
        const transformedData = response.data.data.map((item, index) => ({
          sNo: index + 1,
          item_name: item.item_name,
          total_quantity: item.qty
        }));

        setReportData(transformedData);
        toast.success(`Report generated successfully! ${transformedData.length} unique items found.`);
      } else {
        setReportData([]);
        toast.info('No data found for the selected filters.');
      }

      /* OLD API APPROACH - COMMENTED OUT
      // Build where condition with date range and mode
      let whereCondition = {
        dte: fromDateStr,
        dte: toDateStr
      };

      // Add mode condition based on saleType selection
      if (values.saleType !== 'all') {
        if (values.saleType === 'cash') {
          whereCondition.mode = 'Cash';
        } else if (values.saleType === 'entertainment') {
          whereCondition.mode = 'Entertainment';
        }
      }

      let data = null;
      let tableName = '';

      // Determine table name based on zone and type
      const zonePrefix = values.zone === 'club' ? '' : values.zone + '_';
      
      if (values.type === 'bar') {
        tableName = `${zonePrefix}bar_cart`;
      } else if (values.type === 'food') {
        tableName = `${zonePrefix}food_cart`;
      } else if (values.type === 'ad') {
        tableName = `${zonePrefix}bar_cart`;
      }

      // Fetch data from the dynamically determined table
      data = await fetchData(tableName, null, 'id DESC', whereCondition);

      if (data && data.length > 0) {
        // Group data by item_name and sum quantities
        const groupedData = {};
        data.forEach(item => {
          const itemName = item.item_name || 'Unknown';
          const quantity = parseInt(item.quantity) || 0;
          
          if (groupedData[itemName]) {
            groupedData[itemName] += quantity;
          } else {
            groupedData[itemName] = quantity;
          }
        });

        // Transform grouped data into array with S.No.
        const transformedData = Object.entries(groupedData).map((entry, index) => ({
          sNo: index + 1,
          item_name: entry[0],
          total_quantity: entry[1]
        }));

        setReportData(transformedData);
        toast.success(`Report generated successfully! ${transformedData.length} unique items found.`);
      } else {
        setReportData([]);
        toast.info('No data found for the selected filters.');
      }
      */
      
      // Store filter parameters for export
      setFilterParams({
        type: values.type,
        zone: values.zone,
        saleType: values.saleType,
        fromDate: fromDateStr,
        toDate: toDateStr
      });
      
    } catch (err) {
      console.error('Error generating report:', err);
      toast.error('Error generating report: ' + err.message);
      setReportData(null);
    } finally {
      setLoading(false);
      setDataLoading(false);
    }
  };

  if (accessDenied) {
    return (
      <div style={{ padding: '20px' }}>
        <Alert
          message="Access Denied"
          description="This page is only available for club store manager accounts."
          type="error"
          showIcon
          action={
            <Button 
              type="primary" 
              onClick={() => {
                localStorage.clear();
                sessionStorage.clear();
                navigate('/login');
              }}
            >
              Go to Login
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <>
      <ToastContainer />
      
      {/* Floating Back Button */}
      <Button
        type="primary"
        icon={<HomeOutlined />}
        shape="circle"
        size="large"
        style={{ 
          position: 'fixed',
          right: 24, 
          bottom: 100,
          width: 56,
          height: 56,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          zIndex: 1000,
          backgroundColor: '#1890ff'
        }}
        onClick={() => navigate('/dashboard/clubstoremanager')}
        title="Back to Bar Sales Report"
      />

      {/* Floating Logout Button */}
      <Button
        type="primary"
        icon={<LogoutOutlined />}
        shape="circle"
        size="large"
        danger
        style={{ 
          position: 'fixed',
          right: 24, 
          bottom: 24,
          width: 56,
          height: 56,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          zIndex: 1000
        }}
        onClick={handleLogout}
        title="Logout"
      />

      <div className="club-store-manager-report" style={{ padding: '0', minHeight: '100vh', background: '#f5f5f5', width: '100%' }}>
        {/* Header Section */}
        <div style={{ backgroundColor: '#001f3f', color: '#fff', padding: '30px 20px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)' }}>
          <Row gutter={[16, 16]} style={{ margin: 0, width: '100%', maxWidth: '1400px', margin: '0 auto' }}>
            <Col xs={24} sm={12}>
              <h1 style={{ margin: '0 0 10px 0', fontSize: '28px', fontWeight: 'bold', color: '#fff' }}>
                <BarChartOutlined style={{ marginRight: '10px' }} />
                Club Store Manager Report
              </h1>
              <p style={{ margin: 0, fontSize: '14px', opacity: 1, color: '#fff' }}>Analyze and export your store sales data with ease</p>
            </Col>
            <Col xs={24} sm={12} style={{ textAlign: 'right', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
              <Space>
                <ShoppingCartOutlined style={{ fontSize: '24px' }} />
                <span style={{ fontSize: '16px' }}>Welcome, Manager</span>
              </Space>
            </Col>
          </Row>
        </div>

        <Row gutter={[0, 0]} style={{ margin: 0, width: '100%' }}>
          <Col span={24} style={{ padding: '20px' }}>
            {/* Summary Statistics - Show when data is available */}
            {reportData && reportData.length > 0 && (
              <Row gutter={[16, 16]} style={{ marginBottom: '30px' }}>
                <Col xs={24} sm={12} md={6}>
                  <Card style={{ textAlign: 'center', borderTop: '4px solid #1890ff' }}>
                    <Statistic
                      title="Total Items"
                      value={reportData.length}
                      prefix={<ShoppingCartOutlined />}
                      valueStyle={{ color: '#1890ff', fontSize: '28px', fontWeight: 'bold' }}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Card style={{ textAlign: 'center', borderTop: '4px solid #52c41a' }}>
                    <Statistic
                      title="Total Quantity"
                      value={reportData.reduce((sum, item) => sum + Number(item.total_quantity || 0), 0)}
                      valueStyle={{ color: '#52c41a', fontSize: '28px', fontWeight: 'bold' }}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Card style={{ textAlign: 'center', borderTop: '4px solid #faad14' }}>
                    <Statistic
                      title="Avg per Item"
                      value={(reportData.reduce((sum, item) => sum + Number(item.total_quantity || 0), 0) / reportData.length).toFixed(2)}
                      valueStyle={{ color: '#faad14', fontSize: '28px', fontWeight: 'bold' }}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Card style={{ textAlign: 'center', borderTop: '4px solid #f5222d' }}>
                    <Statistic
                      title="Report Status"
                      value="Ready"
                      valueStyle={{ color: '#f5222d', fontSize: '24px', fontWeight: 'bold' }}
                    />
                  </Card>
                </Col>
              </Row>
            )}

            <Card title="Report Generator" className="report-form-card" style={{ marginBottom: '20px' }}>
              <Form
                form={form}
                layout="vertical"
                onFinish={handleSearch}
                autoComplete="off"
              >
                <Row gutter={[16, 16]}>
                  {/* Type */}
                  <Col xs={24} sm={12} md={8} lg={6}>
                    <Form.Item
                      label="Type"
                      name="type"
                      rules={[{ required: true, message: 'Please select a type' }]}
                    >
                      <Select
                        placeholder="Select Type"
                        options={[
                          { label: 'Ad', value: 'ad' },
                          { label: 'Bar', value: 'bar' },
                          { label: 'Food', value: 'food' }
                        ]}
                      />
                    </Form.Item>
                  </Col>

                  {/* Zone */}
                  <Col xs={24} sm={12} md={8} lg={6}>
                    <Form.Item
                      label="Zone"
                      name="zone"
                      rules={[{ required: true, message: 'Please select a zone' }]}
                      initialValue="club"
                    >
                      <Select
                        placeholder="Select Zone"
                        options={[
                          { label: 'Club', value: 'club' },
                          { label: 'Skybar', value: 'skybar' },
                          { label: 'Pool', value: 'pool' }
                        ]}
                      />
                    </Form.Item>
                  </Col>

                  {/* Sale Type */}
                  <Col xs={24} sm={12} md={8} lg={6}>
                    <Form.Item
                      label="Sale Type"
                      name="saleType"
                      rules={[{ required: true, message: 'Please select a sale type' }]}
                    >
                      <Select
                        placeholder="Select Sale Type"
                        options={[
                          { label: 'Cash Sale', value: 'cash' },
                          { label: 'Entertainment', value: 'entertainment' },
                          { label: 'All', value: 'all' }
                        ]}
                      />
                    </Form.Item>
                  </Col>

                  {/* Search Button */}
                  <Col xs={24} sm={12} md={8} lg={6} style={{ display: 'flex', alignItems: 'flex-end' }}>
                    <Form.Item style={{ width: '100%', marginBottom: 0 }}>
                      <Button
                        type="primary"
                        icon={<SearchOutlined />}
                        loading={loading}
                        htmlType="submit"
                        block
                        style={{ backgroundColor: '#FFC107', borderColor: '#FFC107', color: '#000' }}
                      >
                        Search
                      </Button>
                    </Form.Item>
                  </Col>
                </Row>

                {/* Date Range */}
                <Row gutter={[16, 16]}>
                  <Col xs={24} md={12} lg={12}>
                    <Form.Item
                      label="Select Date Range"
                      name="dateRange"
                      rules={[{ required: true, message: 'Please select a date range' }]}
                    >
                      <DatePicker.RangePicker
                        style={{ width: '100%' }}
                        format="YYYY-MM-DD"
                        placeholder={['From', 'To']}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Form>

              {/* Info Box */}
              <div style={{
                marginTop: '20px',
                padding: '15px',
                backgroundColor: '#f0f2f5',
                borderRadius: '4px',
                borderLeft: '4px solid #1890ff'
              }}>
                <p style={{ margin: 0, color: '#666' }}>
                  <strong>Instructions:</strong> Select the type, zone, sale type, and date range to generate the report. Click the Search button to proceed.
                </p>
              </div>

              {/* Results Table */}
              {dataLoading && (
                <div style={{ textAlign: 'center', marginTop: '30px', padding: '40px' }}>
                  <Spin size="large" tip="Loading data..." />
                </div>
              )}

              {reportData && !dataLoading && (
                <div style={{ marginTop: '30px' }}>
                  <Card title={`Report Results (${reportData.length} unique items)`} bordered={false}>
                    {/* Export Buttons */}
                    <Row gutter={[16, 16]} style={{ marginBottom: '20px' }}>
                      <Col>
                        <Space>
                          <Button 
                            type="primary" 
                            icon={<FilePdfOutlined />}
                            onClick={exportToPDF}
                            style={{ backgroundColor: '#d32f2f' }}
                          >
                            Export PDF
                          </Button>
                          <Button 
                            type="primary" 
                            icon={<FileExcelOutlined />}
                            onClick={exportToExcel}
                            style={{ backgroundColor: '#388e3c' }}
                          >
                            Export Excel
                          </Button>
                          <Button 
                            type="primary" 
                            icon={<PrinterOutlined />}
                            onClick={thermalPrint}
                            style={{ backgroundColor: '#f57c00' }}
                          >
                            Thermal Print
                          </Button>
                        </Space>
                      </Col>
                    </Row>

                    {/* Report Info Header */}
                    <div style={{
                      marginBottom: '20px',
                      padding: '12px',
                      backgroundColor: '#f0f2f5',
                      borderRadius: '4px',
                      borderLeft: '4px solid #1890ff'
                    }}>
                      <Row gutter={[16, 8]}>
                        <Col xs={24} sm={12} md={8}>
                          <strong>Generated:</strong> {new Date().toLocaleString()}
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                          <strong>Period:</strong> {filterParams?.fromDate} to {filterParams?.toDate}
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                          <strong>Type:</strong> {filterParams?.type?.toUpperCase()} | <strong>Zone:</strong> {filterParams?.zone?.toUpperCase()}
                        </Col>
                      </Row>
                    </div>

                    {/* Data Table */}
                    <Table
                      dataSource={reportData ? [...reportData].sort((a, b) => a.item_name.localeCompare(b.item_name)) : []}
                      columns={[
                        {
                          title: 'S.No.',
                          key: 'sNo',
                          width: 80,
                          align: 'center',
                          render: (text, record, index) => index + 1
                        },
                        {
                          title: 'Item Name',
                          dataIndex: 'item_name',
                          key: 'item_name',
                          sorter: (a, b) => a.item_name.localeCompare(b.item_name)
                        },
                        {
                          title: 'Total Quantity',
                          dataIndex: 'total_quantity',
                          key: 'total_quantity',
                          width: 150,
                          align: 'center',
                          sorter: (a, b) => a.total_quantity - b.total_quantity
                        }
                      ]}
                      pagination={{
                        pageSize: 15,
                        total: reportData.length,
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                        showSizeChanger: true,
                        pageSizeOptions: ['10', '15', '20', '50', '100']
                      }}
                      scroll={{ x: 400 }}
                      rowKey={(record) => record.sNo}
                      size="small"
                      bordered
                      style={{ fontSize: '13px' }}
                    />
                  </Card>
                </div>
              )}
            </Card>
          </Col>
        </Row>
      </div>
    </>
  );
};

export default ClubStoreManagerReport;
